import React, { useState } from "react";
import { Badge, Card, IconGlyph, SectionLabel } from "./ProfileDashboardUI";

const EditProfileForm = ({ user, tokens, onSave, onCancel }) => {
  const [form, setForm] = useState({
    name: user.name || "",
    email: user.email || "",
    phone: user.phone || "",
    role: user.role || "",
  });
  const setValue = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));

  const inputStyle = {
    backgroundColor: tokens.card,
    border: `1px solid ${tokens.lineStrong}`,
    color: tokens.ink,
  };

  return (
    <div className="space-y-3">
      {[
        { label: "Full name", key: "name", type: "text" },
        { label: "Email", key: "email", type: "email" },
        { label: "Phone", key: "phone", type: "tel" },
        { label: "Designation", key: "role", type: "text" },
      ].map(({ label, key, type }) => (
        <div key={key}>
          <label className="block text-[11px] font-semibold mb-1" style={{ color: tokens.faint }}>{label}</label>
          <input
            type={type}
            value={form[key]}
            onChange={setValue(key)}
            className="w-full px-3 py-2 rounded-lg text-[13px] outline-none focus:ring-2"
            style={inputStyle}
          />
        </div>
      ))}
      <div className="flex gap-2 pt-1">
        <button
          onClick={() => onSave(form)}
          className="flex-1 py-2 text-white text-[13px] font-medium rounded-lg transition-colors"
          style={{ backgroundColor: tokens.primary }}
        >
          Save changes
        </button>
        <button
          onClick={onCancel}
          className="flex-1 py-2 border text-[13px] rounded-lg transition-colors"
          style={{ borderColor: tokens.lineStrong, color: tokens.muted, backgroundColor: tokens.card }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const ContactLine = ({ icon, children, tokens }) => (
  <span className="inline-flex min-w-0 items-center gap-1 text-[12px] font-medium" style={{ color: tokens.muted }}>
    <IconGlyph name={icon} className="h-3.5 w-3.5" color={tokens.muted} />
    <span className="truncate">{children || "N/A"}</span>
  </span>
);

const EmptyHierarchy = ({ tokens, message = "No approval hierarchy is configured for this profile yet." }) => (
  <div
    className="rounded-lg p-4 text-[12px]"
    style={{ backgroundColor: tokens.cardSoft, color: tokens.muted, border: `1px dashed ${tokens.lineStrong}` }}
  >
    {message}
  </div>
);

const AccountDetailTile = ({ icon, label, value, badge, badgeVariant = "gray", tokens }) => (
  <div
    className="flex min-w-0 items-stretch gap-3 rounded-xl p-3"
    style={{ backgroundColor: tokens.cardSoft, border: `1px solid ${tokens.line}` }}
  >
    <div
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
      style={{ backgroundColor: tokens.card }}
    >
      <IconGlyph name={icon} className="h-4 w-4" color={tokens.faint} />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: tokens.faint }}>
        {label}
      </p>
      <div className="mt-1 min-w-0">
        <span className="truncate text-[13px] font-semibold" style={{ color: tokens.ink }}>
          {value || "N/A"}
        </span>
      </div>
    </div>
    {badge && (
      <div className="flex shrink-0 items-end">
        <span
          className="inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold"
          style={
            badgeVariant === "green"
              ? { backgroundColor: "#DDF7C8", color: "#255F08", border: "1px solid #8CCF5A" }
              : { backgroundColor: tokens.card, color: tokens.muted, border: `1px solid ${tokens.lineStrong}` }
          }
        >
          {badge}
        </span>
      </div>
    )}
  </div>
);

const ApproverMiniCard = ({ person, tokens }) => (
  <div
    className="rounded-lg px-3 py-2"
    style={{ backgroundColor: tokens.card, border: `1px solid ${tokens.line}` }}
  >
    <p className="truncate text-[12px] font-semibold" style={{ color: tokens.ink }}>
      {person?.name || "N/A"}
    </p>
    <div className="mt-1 grid min-w-0 grid-cols-1 gap-1 sm:grid-cols-2">
      <ContactLine icon="mail" tokens={tokens}>{person?.email}</ContactLine>
      <ContactLine icon="phone" tokens={tokens}>{person?.mobile}</ContactLine>
    </div>
  </div>
);

const ApproverCategoryCard = ({ group, tokens }) => (
  <div
    className="rounded-xl p-3"
    style={{ backgroundColor: tokens.card, border: `1px solid ${tokens.line}` }}
  >
    <ApproverMiniCard person={group.approver} tokens={tokens} />
    <div className="mt-3 flex flex-wrap gap-2">
      {(group.divisions || []).map((division) => (
        <span
          key={`${group.approver?.userId}-${division}`}
          className="rounded-full px-2.5 py-1 text-[11px] font-semibold"
          style={{ backgroundColor: tokens.primaryLight, color: tokens.primary, border: `1px solid ${tokens.primaryMid}` }}
        >
          {division}
        </span>
      ))}
    </div>
  </div>
);

const ApprovalStep = ({ levelLabel, title, subtitle, children, isLast, tokens }) => (
  <div className="relative flex gap-4 pb-5 last:pb-0">
    {!isLast && (
      <div
        className="absolute bottom-0 left-[18px] top-10 w-px"
        style={{ backgroundColor: tokens.lineStrong }}
      />
    )}

    <div
      className="relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[12px] font-bold"
      style={{ backgroundColor: tokens.primaryLight, color: tokens.primary, border: `1px solid ${tokens.primaryMid}` }}
    >
      {levelLabel}
    </div>

    <div
      className="min-w-0 flex-1 rounded-xl p-4"
      style={{ backgroundColor: tokens.cardSoft, border: `1px solid ${tokens.line}` }}
    >
      <div className="mb-3">
        <p className="text-[14px] font-semibold" style={{ color: tokens.ink }}>
          {title}
        </p>
        <p className="mt-1 text-[11px]" style={{ color: tokens.faint }}>
          {subtitle}
        </p>
      </div>

      {children}
    </div>
  </div>
);

const ApprovalHierarchy = ({ user, tokens }) => {
  const managers = user?.managers || [];
  const approvalLevels = user?.approvalLevels || [];
  const hasHierarchy = managers.length > 0 || approvalLevels.length > 0;

  if (!hasHierarchy) {
    return (
      <EmptyHierarchy
        tokens={tokens}
        message={
          user?.isCurrentUserApprover
            ? "You are already at the highest configured approval level for this entity."
            : "No approval hierarchy is configured for this profile yet."
        }
      />
    );
  }

  const steps = [
    ...(managers.length > 0
      ? [{
        key: "initial-manager",
        levelLabel: "L1",
        title: "Initial manager approval",
        subtitle: "Direct reporting manager approval before threshold levels.",
        content: (
          <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
            {managers.map((manager, index) => (
              <ApproverMiniCard
                key={`manager-${manager.userId || index}`}
                person={manager}
                tokens={tokens}
              />
            ))}
          </div>
        ),
      }]
      : []),
    ...approvalLevels.map((level) => ({
      key: `approval-${level.level}-${level.thresholdLabel}`,
      levelLabel: `L${managers.length + level.level}`,
      title: level.isCategoryBased ? "Category approval" : "Threshold approval",
      subtitle: level.isCategoryBased ? level.thresholdLabel : `Applies from ${level.thresholdLabel}`,
      content: (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {(level.approverGroups || []).map((group) => (
            <ApproverCategoryCard
              key={`${level.level}-${group.approver?.userId}`}
              group={group}
              tokens={tokens}
            />
          ))}
        </div>
      ),
    })),
  ];

  return (
    <div>
      {steps.map((step, index) => (
        <ApprovalStep
          key={step.key}
          levelLabel={step.levelLabel}
          title={step.title}
          subtitle={step.subtitle}
          isLast={index === steps.length - 1}
          tokens={tokens}
        >
          {step.content}
        </ApprovalStep>
      ))}

      {false && approvalLevels.flatMap((level) =>
        level.businessDivisions.map((division) => (
          <ApproverMiniCard
            key={`approval-${level.level}-${level.thresholdLabel}-${division.division}`}
            title={division.division}
            subtitle={`Threshold approval L${managers.length + level.level} • Applies from ${level.thresholdLabel}`}
            people={division.approvers}
            badge={`${division.approvers.length} approver${division.approvers.length > 1 ? "s" : ""}`}
            badgeVariant="purple"
            tokens={tokens}
          />
        )),
      )}
    </div>
  );
};

const ProfileAccount = ({ user, editing, tokens, onCancelEdit, onSave }) => (
  <>
    <SectionLabel tokens={tokens}>Profile & account</SectionLabel>
    <div className="space-y-3">
      <Card tokens={tokens} className="h-fit">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[13px] font-semibold" style={{ color: tokens.ink }}>Account details</span>
        </div>
        {editing ? (
          <EditProfileForm user={user} tokens={tokens} onSave={onSave} onCancel={onCancelEdit} />
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            <AccountDetailTile icon="user" label="Full name" value={user.name} tokens={tokens} />
            <AccountDetailTile icon="award" label="Designation" value={user.designation} tokens={tokens} />
            <AccountDetailTile icon="mail" label="Email" value={user.email} tokens={tokens} />
            <AccountDetailTile
              icon="phone"
              label="Phone"
              value={user.phone}
              badge={user.phoneVerified ? "Verified" : undefined}
              badgeVariant="green"
              tokens={tokens}
            />
            <AccountDetailTile icon="building" label="Company" value={user.company} tokens={tokens} />
            <AccountDetailTile
              icon="calendar"
              label="Member since"
              value={user.memberSince}
              badge={user.kycDone ? "KYC done" : undefined}
              tokens={tokens}
            />
          </div>
        )}
      </Card>

      <Card tokens={tokens}>
        <div className="flex items-center justify-between mb-3">
          <span className="text-[13px] font-semibold" style={{ color: tokens.ink }}>Approval hierarchy</span>
          <Badge variant={user?.managers?.length || user?.approvalLevels?.length ? "blue" : "gray"} tokens={tokens}>
            {user?.managers?.length || user?.approvalLevels?.length ? "Configured" : "Not configured"}
          </Badge>
        </div>
        <ApprovalHierarchy user={user} tokens={tokens} />
      </Card>
    </div>
  </>
);

export default ProfileAccount;
